import type { SVGProps } from "react";

/**
 * The icon set.
 *
 * Drawn here rather than pulled from a library, for three reasons: nothing loads
 * from a CDN (a DSGVO requirement in Austria, not a preference), the whole set
 * shares one 16 px grid and 1.5 px stroke so a toolbar does not look assembled from
 * parts, and the set stays small — an application with sixty icons has an
 * information-architecture problem, not an icon problem.
 *
 * Icons never carry meaning alone; every one of them sits next to a text label.
 */

type Props = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 16, children, ...rest }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconHaus = (p: Props) => (
  <Icon {...p}>
    <path d="M2 6.5 8 2l6 4.5V13a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6.5Z" />
    <path d="M6 14v-4h4v4" />
  </Icon>
);

export const IconListe = (p: Props) => (
  <Icon {...p}>
    <path d="M5.5 4h8M5.5 8h8M5.5 12h8M2.5 4h.01M2.5 8h.01M2.5 12h.01" />
  </Icon>
);

export const IconBuch = (p: Props) => (
  <Icon {...p}>
    <path d="M3 2.5h7a2 2 0 0 1 2 2V14H5a2 2 0 0 1-2-2V2.5Z" />
    <path d="M12 4.5h1.5V14" />
  </Icon>
);

export const IconBank = (p: Props) => (
  <Icon {...p}>
    <path d="M2 6.5 8 3l6 3.5" />
    <path d="M3.5 7v5M6.5 7v5M9.5 7v5M12.5 7v5" />
    <path d="M2 13.5h12" />
  </Icon>
);

export const IconTropfen = (p: Props) => (
  <Icon {...p}>
    <path d="M8 2s4 4.4 4 7a4 4 0 0 1-8 0c0-2.6 4-7 4-7Z" />
  </Icon>
);

export const IconUhr = (p: Props) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="6" />
    <path d="M8 4.5V8l2.5 1.5" />
  </Icon>
);

export const IconAkte = (p: Props) => (
  <Icon {...p}>
    <path d="M3 2.5h5l1.5 2H13v9H3v-11Z" />
    <path d="M5.5 8h5M5.5 10.5h3" />
  </Icon>
);

export const IconDiagramm = (p: Props) => (
  <Icon {...p}>
    <path d="M2.5 13.5V9M6.5 13.5V4M10.5 13.5V7M14 13.5v-3" />
  </Icon>
);

export const IconSuche = (p: Props) => (
  <Icon {...p}>
    <circle cx="7" cy="7" r="4.5" />
    <path d="m10.5 10.5 3 3" />
  </Icon>
);

export const IconAgent = (p: Props) => (
  <Icon {...p}>
    <path d="M8 2.5 9.4 6l3.6 1.4L9.4 8.8 8 12.4 6.6 8.8 3 7.4 6.6 6 8 2.5Z" />
  </Icon>
);

export const IconMehr = (p: Props) => (
  <Icon {...p}>
    <path d="M3.5 8h.01M8 8h.01M12.5 8h.01" />
  </Icon>
);

export const IconPfeilRechts = (p: Props) => (
  <Icon {...p}>
    <path d="m6 3.5 4.5 4.5L6 12.5" />
  </Icon>
);

export const IconAbmelden = (p: Props) => (
  <Icon {...p}>
    <path d="M6 13.5H3.5v-11H6" />
    <path d="M9.5 10.5 12.5 8 9.5 5.5M12.5 8H6" />
  </Icon>
);

export const IconWaage = (p: Props) => (
  <Icon {...p}>
    <path d="M8 3v11M4.5 3.8h7" />
    <path d="M3 11a2 2 0 0 0 4 0L5 6l-2 5ZM9 11a2 2 0 0 0 4 0l-2-5-2 5Z" />
  </Icon>
);

export const IconBrief = (p: Props) => (
  <Icon {...p}>
    <rect x="2" y="3.5" width="12" height="9" rx="1" />
    <path d="m2.5 4.5 5.5 4 5.5-4" />
  </Icon>
);

export const IconDokument = (p: Props) => (
  <Icon {...p}>
    <path d="M9 2H4.5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h7a1 1 0 0 0 1-1V5.5L9 2Z" />
    <path d="M9 2v3.5h3.5" />
    <path d="M6 9h4M6 11.5h4" />
  </Icon>
);

export const IconPersonen = (p: Props) => (
  <Icon {...p}>
    <circle cx="6" cy="5.5" r="2.5" />
    <path d="M1.5 13.5c0-2.2 2-3.5 4.5-3.5s4.5 1.3 4.5 3.5" />
    <path d="M11 3.4a2.5 2.5 0 0 1 0 4.7M12.2 10.4c1.4.5 2.3 1.6 2.3 3.1" />
  </Icon>
);

export const IconMahnung = (p: Props) => (
  <Icon {...p}>
    <path d="M8 1.8 14.7 13a1 1 0 0 1-.9 1.5H2.2A1 1 0 0 1 1.3 13L8 1.8Z" />
    <path d="M8 6v3.5M8 12h.01" />
  </Icon>
);

export const IconAbrechnung = (p: Props) => (
  <Icon {...p}>
    <path d="M4 1.5h8a1 1 0 0 1 1 1v12l-2-1.2-1.5 1.2L8 13.3l-1.5 1.2L5 13.3 3 14.5v-12a1 1 0 0 1 1-1Z" />
    <path d="M5.5 5h5M5.5 8h5M5.5 10.5h3" />
  </Icon>
);

export const IconVerwaltung = (p: Props) => (
  <Icon {...p}>
    <circle cx="8" cy="8" r="2.2" />
    <path d="M8 1.5v1.8M8 12.7v1.8M14.5 8h-1.8M3.3 8H1.5M12.6 3.4l-1.3 1.3M4.7 11.3l-1.3 1.3M12.6 12.6l-1.3-1.3M4.7 4.7 3.4 3.4" />
  </Icon>
);
